import React, { useState, useMemo } from 'react';
import { TrendingUp, Truck, FileText, CheckCircle, Download, Calendar, Users, Filter, ArrowUpRight, Search, PhoneCall, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import { formatAppDate, formatAppDateOnly } from '../../utils/dateUtils';
import * as XLSX from 'xlsx';
import Dropdown from '../../components/Dropdown';
import DateFilter from '../../components/DateFilter';
import AddLead from '../leads/AddLead';
import ViewDeliveryModal from '../deliveries/ViewDeliveryModal';
import TakeActionModal from '../leads/TakeActionModal';
import { useAuth } from '../../context/AuthContext';
import { getStatusColors } from '../../utils/statusUtils';
import PrinterLoader from '../../components/PrinterLoader';

const Reports = () => {
  const { user, hasPermission } = useAuth();
  const [dateFilter, setDateFilter] = useState('this_month');
  const [leadsData, setLeadsData] = useState([]);
  const [followupsData, setFollowupsData] = useState([]);
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [masterSources, setMasterSources] = useState([]);
  const [masterStatuses, setMasterStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actionLogs, setActionLogs] = useState([]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [leadsRes, followupsRes, logsRes, usersRes, sourcesRes, statusesRes, deliveriesRes] = await Promise.all([
          api.get('/leads'),
          api.get('/followups').catch(() => ({ data: [] })),
          api.get('/logs').catch(() => ({ data: [] })),
          api.get('/auth/users').catch(() => ({ data: [] })),
          api.get('/masters/lead-sources').catch(() => ({ data: [] })),
          api.get('/masters/status').catch(() => ({ data: [] })),
          api.get('/deliveries').catch(() => ({ data: [] }))
        ]);
        
        setMasterSources((sourcesRes.data || []).filter(s => s.status === 'Active').map(s => s.name));
        setMasterStatuses((statusesRes.data || []).filter(s => s.status === 'Active').map(s => s.name));

        const users = usersRes.data || [];
        const userRoleMap = {};
        users.forEach(u => {
          if (u.name) {
            userRoleMap[u.name.toLowerCase()] = u.role;
          }
        });

        const logs = logsRes.data || [];
        const updatedLogs = logs.map(log => {
          if (log.user) {
            const actualRole = userRoleMap[log.user.toLowerCase()];
            if (actualRole) {
              return { ...log, role: actualRole };
            }
          }
          return log;
        });
        
        setActionLogs(updatedLogs);
        
        const formattedLeads = leadsRes.data.map(lead => ({
          id: lead.id,
          name: lead.companyName,
          contact: lead.contactPerson,
          phone: lead.phone,
          machine: lead.machine?.name || 'Unknown',
          machineModel: lead.machineModel || '',
          type: lead.leadType?.name ? lead.leadType.name : (lead.otherInterest ? `Other (${lead.otherInterest})` : 'Unknown'),
          assignee: lead.user?.name || 'Unassigned',
          assigneeRole: lead.user?.role || 'User',
          status: lead.status?.name || 'New',
          source: lead.leadSource?.name || 'Unknown',
          createdAt: lead.createdAt,
          createdBy: lead.createdBy,
          updatedAt: lead.updatedAt,
          followupsCount: (lead.followups || [])?.length || 0,
          completedBy: ['won', 'completed', 'complete'].includes(lead.status?.name?.toLowerCase() || '') ? (lead.user?.name || 'Unknown') : 'N/A',
          original: lead
        }));
        
        setDeliveriesData(Array.isArray(deliveriesRes.data) ? deliveriesRes.data : (deliveriesRes.data?.data || []));
        const formattedFollowups = followupsRes.data.map(f => ({
          id: f.id,
          leadId: f.leadId,
          leadName: f.lead?.companyName || 'Unknown Lead',
          contact: f.lead?.contactPerson || '',
          phone: f.lead?.phone || '',
          machineModel: f.lead?.machineModel || '',
          scheduledAt: formatAppDate(f.date || f.createdAt),
          rawDate: f.date || f.createdAt,
          purpose: f.notes || f.purpose || 'Follow-up',
          type: f.type,
          status: f.lead?.status?.name || f.status || 'Pending',
          assignee: f.lead?.user?.name || 'Unknown',
          assigneeRole: f.lead?.user?.role || 'User',
          createdAt: f.createdAt,
          updatedAt: f.updatedAt,
          createdBy: f.createdBy,
          original: f
        }));

        let finalLeads = formattedLeads;
        if (user?.role !== 'Admin' && !hasPermission('leads:read_all')) {
          finalLeads = formattedLeads.filter(l => l.assignee === user?.name || l.createdBy === user?.name);
        }
        setLeadsData(finalLeads);

        let finalFollowups = formattedFollowups;
        if (user?.role !== 'Admin' && !hasPermission('leads:read_all')) {
          finalFollowups = formattedFollowups.filter(f => f.assignee === user?.name || finalLeads.some(l => l.id === f.leadId));
        }
        setFollowupsData(finalFollowups);
      } catch (err) {
        console.error('Failed to fetch reports data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const [userFilter, setUserFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);
  const [isViewMode, setIsViewMode] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionLead, setActionLead] = useState(null);
  const [deleteLeadId, setDeleteLeadId] = useState(null);
  const [viewingDelivery, setViewingDelivery] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);

  const fetchLeadsAndFollowups = async () => {
    try {
      const [leadsRes, followupsRes, deliveriesRes] = await Promise.all([
        api.get(`/leads?period=${dateFilter}`),
        api.get(`/followups/all?period=${dateFilter}`),
        api.get(`/deliveries?period=${dateFilter}`)
      ]);
      
      const formattedLeads = (leadsRes.data || []).map(lead => ({
        id: lead.id,
        name: lead.companyName,
        contact: lead.contactPerson,
        phone: lead.phone,
        email: lead.email,
        machine: lead.machine?.name ? `${lead.machine.name}${lead.machineModel ? ' - ' + lead.machineModel : ''}` : 'Unknown',
        type: lead.leadType?.name || lead.interestType || 'Unknown',
        assignee: lead.user?.name || 'Unassigned',
        assigneeRole: lead.user?.role?.name || 'User',
        status: lead.status?.name || 'New Lead',
        source: lead.leadSource?.name || 'Unknown',
        createdAt: lead.createdAt,
        updatedAt: lead.updatedAt,
        createdBy: lead.createdBy,
        followupsCount: (lead.original?.followups || [])?.length || 0,
        original: lead
      }));
      setLeadsData(formattedLeads);

      setFollowupsData(followupsRes.data || []);
      setDeliveriesData(Array.isArray(deliveriesRes.data) ? deliveriesRes.data : (deliveriesRes.data?.data || []));
      
      // Update viewing lead if it's open
      if (viewingLead) {
        const updatedLead = leadsRes.data.find(l => l.id === viewingLead.id);
        if (updatedLead) setViewingLead(updatedLead);
      }
    } catch (err) {
      console.error('Failed to fetch data', err);
    }
  };

  const handleOpenLead = (leadId, leadName) => {
    const lead = leadsData.find(l => l.id === leadId) || followupsData.find(f => f.leadId === leadId)?.lead;
    if (lead) {
      setViewingLead(lead);
      setIsViewMode(true);
      setShowModal(true);
    } else {
      api.get(`/leads/${leadId}`).then(res => {
        setViewingLead(res.data);
        setIsViewMode(true);
        setShowModal(true);
      }).catch(console.error);
    }
  };

  const handleDelete = async (id) => {
    setDeleteLeadId(id);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/leads/${deleteLeadId}`);
      setShowModal(false);
      setDeleteLeadId(null);
      fetchLeadsAndFollowups();
    } catch (err) {
      console.error(err);
      alert('Failed to delete lead');
    }
  };

  // List Pagination States
  const [newLeadsPage, setNewLeadsPage] = useState(1);
  const [followupPage, setFollowupPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [deliveryPage, setDeliveryPage] = useState(1);
  const listItemsPerPage = 10;

  // Derived Data
  const roleFilteredLeads = leadsData;
  const filteredLeads = roleFilteredLeads;
  
  const allNewLeads = filteredLeads.filter(l => l.status === 'New Lead' || l.status === 'New' || l.status === 'Pending');
  const newLeads = allNewLeads.slice((newLeadsPage - 1) * listItemsPerPage, newLeadsPage * listItemsPerPage);

  console.log('first lead followups:', filteredLeads[0]?.original?.followups); const allFollowUpCustomers = filteredLeads
    .filter(l => l.status?.toLowerCase() === 'schedule' || l.status?.toLowerCase() === 'reschedule')
    .map(l => {
      const validFollowups = (l.original?.followups || []).filter(f => f.type !== 'System');
      const latestFollowup = validFollowups.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      return {
        id: l.id,
        name: l.name,
        contact: l.contact,
        phone: l.phone,
        machineModel: l.machine,
        nextFollowUp: latestFollowup ? formatAppDate(latestFollowup.date) : 'N/A',
        source: l.source,
        type: l.type,
        status: l.status,
        assignee: l.assignee,
        createdAt: formatAppDate(l.createdAt),
        original: l.original
      };
    });
  const followUpCustomers = allFollowUpCustomers.slice((followupPage - 1) * listItemsPerPage, followupPage * listItemsPerPage);
  
  const allCompletedCustomers = filteredLeads.filter(l => ['Won', 'Lost', 'Completed', 'Complete', 'Closed'].includes(l.status)).map(l => ({
    id: l.id,
    name: l.name,
    contact: l.contact,
    phone: l.phone,
    completionDate: formatAppDate(l.updatedAt),
    status: l.status,
    source: l.source,
    machine: l.machine,
    type: l.type,
    assignee: l.assignee,
    createdBy: l.createdBy,
    createdAt: formatAppDate(l.createdAt),
    followupsCount: l.followupsCount,
    completedBy: l.completedBy,
    original: l
  }));
  const completedCustomers = allCompletedCustomers.slice((completedPage - 1) * listItemsPerPage, completedPage * listItemsPerPage);
  const allDeliveries = deliveriesData.map(d => ({
    id: d.id,
    name: d.title || d.partyName || ('Delivery #' + d.id),
    contact: d.partyName || 'N/A',
    phone: d.partyPhone || 'N/A',
    source: d.status,
    date: d.createdAt,
    original: d
  }));
  const deliveriesList = allDeliveries.slice((deliveryPage - 1) * listItemsPerPage, deliveryPage * listItemsPerPage);


  const completedData = filteredLeads.filter(l => ['Won', 'Lost', 'Completed', 'Complete', 'Closed'].includes(l.status));
  
  // Calculate top sources
  const sourceCount = {};
  masterSources.forEach(s => { sourceCount[s] = 0; });
  filteredLeads.forEach(lead => {
    const s = lead.source;
    if (s && s !== 'Unknown') {
      sourceCount[s] = (sourceCount[s] || 0) + 1;
    }
  });
  const topSources = Object.entries(sourceCount);

  // Calculate status counts for charts
  const chartStatusCounts = {};
  masterStatuses.forEach(s => { chartStatusCounts[s] = 0; });
  filteredLeads.forEach(lead => {
    const s = lead.status;
    if (s) {
      chartStatusCounts[s] = (chartStatusCounts[s] || 0) + 1;
    }
  });
  const topStatuses = Object.entries(chartStatusCounts);

  // Status counts
  const statusCounts = {
    followUp: followupsData.length,
    completed: completedData.filter(l => l.status === 'Won').length,
    notInterested: completedData.filter(l => l.status === 'Lost').length
  };

  // Generate dynamic audit logs from leads and followups
  const initialLogs = useMemo(() => {
    let logs = [];
    
    leadsData.forEach(lead => {
      // Lead Creation Event
      logs.push({
        id: `lead-create-${lead.id}`,
        timestamp: new Date(lead.createdAt).getTime(),
        date: formatAppDate(lead.createdAt),
        user: lead.createdBy || lead.assignee || 'Unknown',
        role: lead.assigneeRole,
        actionType: 'Lead Creation',
        action: `Created new lead: ${lead.name}`,
        status: 'Success'
      });
      
      // Lead Update Event
      if (lead.updatedAt && new Date(lead.updatedAt).getTime() - new Date(lead.createdAt).getTime() > 1000) {
        const updateTime = new Date(lead.updatedAt).getTime();
        const hasMatchingFollowup = followupsData.some(f => 
          f.leadId === lead.id && 
          (Math.abs(new Date(f.updatedAt || f.createdAt).getTime() - updateTime) < 5000 || Math.abs(new Date(f.createdAt).getTime() - updateTime) < 5000)
        );
        
        if (!hasMatchingFollowup) {
          logs.push({
            id: `lead-update-${lead.id}-${updateTime}`,
            timestamp: updateTime,
            date: formatAppDate(lead.updatedAt),
            user: lead.assignee,
            role: lead.assigneeRole,
            actionType: 'Lead Update',
            action: `Updated lead details: ${lead.name}`,
            status: 'Success'
          });
        }
      }
    });

    followupsData.forEach(f => {
      if (f.type === 'System') {
        let actionType = 'System Action';
        let actionText = f.purpose;
        
        if (f.purpose?.startsWith('[Assigned]')) {
          actionType = 'Lead Assigned';
          actionText = `Assigned lead ${f.leadName} to ${f.purpose.substring(23)}`;
        } else if (f.purpose?.startsWith('[Status Change] ')) {
          actionType = 'Status Update';
          actionText = f.purpose.substring(16);
        }

        logs.push({
          id: `system-log-${f.id}`,
          timestamp: new Date(f.createdAt).getTime(),
          date: formatAppDate(f.createdAt),
          user: f.createdBy || f.assignee || 'Unknown',
          role: f.assigneeRole || 'System',
          actionType: actionType,
          action: actionText,
          status: 'Success'
        });
        return;
      }

      // Check if this follow-up is the child of a reschedule
      const isChildOfModification = followupsData.some(otherF => {
        if (otherF.id === f.id) return false;
        const isMod = otherF.purpose?.startsWith('[Rescheduled]') || otherF.purpose?.startsWith('[Edited]');
        if (!isMod || !otherF.updatedAt) return false;
        return Math.abs(new Date(otherF.updatedAt).getTime() - new Date(f.createdAt).getTime()) < 5000;
      });

      // 1. Log the initial creation, unless it's a child of a reschedule
      if (!isChildOfModification) {
        logs.push({
          id: `followup-created-${f.id}`,
          timestamp: new Date(f.createdAt).getTime(),
          date: formatAppDate(f.createdAt),
          user: f.createdBy || f.assignee || 'Unknown',
          role: f.assigneeRole,
          actionType: 'Follow-up Scheduled',
          action: `Scheduled follow-up for ${f.leadName} (Scheduled for: ${f.scheduledAt})`,
          status: 'Success'
        });
      }

      // 2. If it was modified (completed/closed/rescheduled), push a SECOND log for that specific action
      if (f.purpose?.includes('[Complete') || f.purpose?.includes('[Closed]') || f.purpose?.includes('[Reschedule') || f.purpose?.includes('[Edit')) {
        let type = '';
        let actionDesc = '';

        if (f.purpose?.includes('[Complete')) {
          type = 'Follow-up Completed';
          actionDesc = `Completed follow-up for ${f.leadName}. Notes: ${f.purpose.replace(/^\[.*?\]\s*/, '')}`;
        } else if (f.purpose?.startsWith('[Closed]')) {
          type = 'Lead Closed';
          actionDesc = `Closed lead ${f.leadName}. ${f.purpose.substring(9)}`;
        } else if (f.purpose?.includes('[Reschedule') || f.purpose?.includes('[Edit')) {
          const isEdit = f.purpose?.includes('[Edit');
          type = isEdit ? 'Follow-up Edited' : 'Follow-up Rescheduled';
          
          // Find the child follow-up to get the new scheduled date
          const childF = followupsData.find(otherF => {
            if (otherF.id === f.id) return false;
            return Math.abs(new Date(f.updatedAt).getTime() - new Date(otherF.createdAt).getTime()) < 5000;
          });
          
          const scheduledForText = childF ? ` (Scheduled for: ${childF.scheduledAt})` : '';
          actionDesc = `${isEdit ? 'Edited' : 'Rescheduled'} follow-up for ${f.leadName}. ${f.purpose.replace(/^\[.*?\]\s*/, '')}${scheduledForText}`;
        }

        logs.push({
          id: `followup-action-${f.id}`,
          timestamp: new Date(f.updatedAt || f.createdAt).getTime(),
          date: formatAppDate(f.updatedAt || f.createdAt),
          user: f.assignee || f.createdBy || 'Unknown',
          role: f.assigneeRole,
          actionType: type,
          action: actionDesc,
          status: 'Success'
        });
      }
    });

    // Add backend persistent logs (e.g. deletions)
    actionLogs.forEach(al => {
      // Find the role if we know the user
      let resolvedRole = 'User';
      const leadWithUser = leadsData.find(l => l.assignee === al.user || l.createdBy === al.user);
      if (leadWithUser) {
        resolvedRole = leadWithUser.assigneeRole;
      }

      logs.push({
        id: `actionlog-${al.id}`,
        timestamp: new Date(al.createdAt).getTime(),
        date: formatAppDate(al.createdAt),
        user: al.user || 'Unknown',
        role: resolvedRole,
        actionType: al.type,
        action: al.action,
        status: 'Success'
      });
    });

    // Deduplicate logs
    const uniqueLogs = [];
    logs.forEach(log => {
       const existingIndex = uniqueLogs.findIndex(existing => {
         const timeDiff = Math.abs(existing.timestamp - log.timestamp);
         if (timeDiff > 60000) return false; // 1 minute window
         
         const isCreation = (existing.actionType === 'Lead Creation' || existing.actionType === 'Lead Created') &&
                            (log.actionType === 'Lead Creation' || log.actionType === 'Lead Created');
                            
         const isAssigned = (existing.actionType === 'Lead Assigned' && log.actionType === 'Lead Assigned');
         
         if (isCreation || isAssigned) {
            if (existing.user === log.user) return true;
         }
         
         if (existing.action === log.action && existing.user === log.user) return true;

         return false;
       });
       
       if (existingIndex === -1) {
         uniqueLogs.push(log);
       } else {
         if (uniqueLogs[existingIndex].role === 'User' && log.role !== 'User') {
             uniqueLogs[existingIndex].role = log.role;
         }
       }
    });

    // Sort descending by time
    uniqueLogs.sort((a, b) => b.timestamp - a.timestamp);
    return uniqueLogs;
  }, [leadsData, followupsData, actionLogs]);
  
  const uniqueUsers = useMemo(() => {
    const users = new Set();
    leadsData.forEach(l => {
      if (l.assignee && l.assignee !== 'Unassigned') users.add(l.assignee);
    });
    followupsData.forEach(f => {
      if (f.assignee && f.assignee !== 'Unknown') users.add(f.assignee);
    });
    return [{ value: 'all', label: 'All Users' }, ...Array.from(users).map(u => ({ value: u.toLowerCase(), label: u }))];
  }, [leadsData, followupsData]);

  // Filtering logic
  const filteredLogs = useMemo(() => {
    let logs = initialLogs;
    if (user?.role !== 'Admin') {
      const uName = user?.name?.toLowerCase() || '';
      logs = logs.filter(log => {
        const lUser = (log.user || '').toLowerCase();
        return lUser.includes(uName) || uName.includes(lUser) || lUser === 'unknown' || lUser === 'unassigned';
      });
    }
    
    return logs.filter(log => {
      if (user?.role === 'Admin' && userFilter !== 'all' && !log.user.toLowerCase().includes(userFilter)) return false;
      
      const logDate = new Date(log.timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === 'today') {
        return logDate >= today;
      }
      if (dateFilter === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return logDate >= yesterday && logDate < today;
      }
      // 'this_month' or 'all' just returns true for now as a simplification
      return true;
    });
  }, [dateFilter, userFilter, user, initialLogs]);

  // Audit Log Pagination
  const [auditPage, setAuditPage] = useState(1);
  const [auditItemsPerPage, setAuditItemsPerPage] = useState(10);
  
  const totalAuditPages = Math.ceil(filteredLogs.length / auditItemsPerPage) || 1;
  const paginatedLogs = filteredLogs.slice((auditPage - 1) * auditItemsPerPage, auditPage * auditItemsPerPage);

  // Handle CSV Downloads
  
  const downloadExcel = (filename, dataRows) => {
    const worksheet = XLSX.utils.json_to_sheet(dataRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, filename);
  };

  const handleDownloadSummary = () => {
    const summaryData = [
      { Metric: 'Total Leads', Value: '2450' },
      { Metric: 'Justdial Leads', Value: '850' },
      { Metric: 'IndiaMart Leads', Value: '1100' },
      { Metric: 'Walk-in Leads', Value: '300' },
      { Metric: 'Blank Leads', Value: '200' },
      { Metric: 'Follow-up Status', Value: '1240' },
      { Metric: 'Completed Status', Value: '312' },
      { Metric: 'Not Interested Status', Value: '450' },
    ];
    downloadExcel(`kpi_summary_${dateFilter}.xlsx`, summaryData);
  };

  const handleDownloadAudit = () => {
    const rows = filteredLogs.map(log => ({
      'Date & Time': log.date,
      'User': log.user,
      'Role': log.role,
      'Action Type': log.actionType,
      'Action Description': log.action,
      'Status': log.status
    }));
    downloadExcel(`audit_report_${dateFilter}.xlsx`, rows);
  };

  const handleDownloadList = (listName, data) => {
    if (data.length === 0) return;
    
    const formattedData = data.map((item, index) => {
      if (listName === 'deliveries') {
        return {
          'Delivery ID': item.id,
          'Title': item.name,
          'Party Name': item.contact,
          'Phone': item.phone,
          'Order No': item.original?.orderNo || 'N/A',
          'Amount': item.original?.amount || 'N/A',
          'Status': item.source,
          'Created By': item.original?.createdBy || 'System',
          'Date': formatAppDate(item.date)
        };
      }

      let rawLead = null;
      let rawFollowup = null;

      if (listName === 'new_leads') {
        rawLead = item.original || item;
      } else if (listName === 'completed') {
        rawLead = item.original?.original || item.original || item;
      } else if (listName === 'followups') {
        // item is from allFollowUpCustomers which maps from leads
        rawLead = item.original?.original || item.original || item;
      }

      if (!rawLead && listName === 'followups') {
        rawLead = {
          id: item.id,
          companyName: item.name,
          contactPerson: item.contact,
          phone: item.phone,
          machineModel: item.machineModel,
          status: { name: item.status },
          user: { name: item.assignee }
        };
      } else if (!rawLead) {
        return {};
      }

      const baseRow = {
        'SR.No': index + 1,
        'Lead No': rawLead.id,
        'Shop Type': rawLead.agencyType || 'N/A',
        'Shop Name': rawLead.companyName || 'N/A',
        'Shop Phone Number': rawLead.phone || 'N/A',
        'Shop Email Address': rawLead.email || 'N/A',
        'Shop Address': [rawLead.address, rawLead.city, rawLead.state, rawLead.pincode].filter(Boolean).join(', ') || 'N/A',
        'Contact Person': rawLead.contactPerson || 'N/A',
        'Contact Person Phone': rawLead.contactPhone || rawLead.phone || 'N/A',
        'Contact Person Email': rawLead.contactEmail || rawLead.email || 'N/A',
        'Interest Type': rawLead.leadType?.name || rawLead.interestType || rawLead.otherInterest || 'N/A',
        'Machine Type': rawLead.machineType || 'N/A',
        'Machine Brand': rawLead.machine?.name || rawLead.machineBrand || 'N/A',
        'Machine Model': rawLead.machineModel || 'N/A',
        'Lead Source': rawLead.leadSource?.name || rawLead.source || 'N/A',
        'status': rawLead.status?.name || rawLead.status || 'N/A',
        'Assign To': rawLead.user?.name || rawLead.assignee || 'Unassigned',
        'Customer Type': rawLead.customerType?.name || 'N/A',
        'Created Date': formatAppDate(rawLead.createdAt)
      };

      if (listName === 'followups') {
        baseRow['Followup Date and Time'] = item.nextFollowUp || 'N/A';
      } else if (listName === 'completed') {
        baseRow['Completed Date'] = formatAppDate(rawLead.updatedAt || item.completionDate);
      }
      
      return baseRow;
    });

    downloadExcel(`${listName}_list_${dateFilter}.xlsx`, formattedData);
  };

  const getStatusColor = (status) => {
    if (status === 'Success' || status === 'Won') return { bg: '#dcfce7', text: '#166534' };
    if (status === 'Pending' || status === 'Lost') return { bg: '#fef3c7', text: '#92400e' };
    return { bg: '#f1f5f9', text: '#475569' };
  };

  const handleView = async (leadSummary) => {
    try {
      const res = await api.get(`/leads/${leadSummary.id}`);
      const realLead = res.data;
      
      setViewingLead({
        ...realLead,
        id: realLead.id,
        name: realLead.companyName,
        contact: realLead.contactPerson,
        phone: realLead.phone,
        email: realLead.email,
        address: realLead.address,
        source: realLead.leadSource?.name || 'Unknown',
        assignee: realLead.user?.name || 'Unassigned',
        machine: realLead.machine?.name || 'N/A',
        type: realLead.leadType?.name ? realLead.leadType.name : (realLead.otherInterest ? `Other (${realLead.otherInterest})` : 'Warm'),
        status: realLead.status?.name || 'Pending',
        createdAt: realLead.createdAt,
        notes: realLead.notes,
        followups: realLead.followups || []
      });
      setIsViewMode(true);
      setShowModal(true);
    } catch (err) {
      console.error("Failed to fetch full lead details", err);
      import('react-hot-toast').then(({ toast }) => toast.error("Error fetching lead: " + String(err)));
      // Fallback
      setViewingLead(leadSummary);
      setIsViewMode(true);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setViewingLead(null);
  };

  return (
    <div>
      {loading && <PrinterLoader />}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Reports & Analytics</h2>
          <p className="text-muted mt-1">Monitor system activity, sources, and lists.</p>
        </div>

        {/* Control Bar */}
        <div className="flex gap-4 items-center">
          <div className="flex items-center gap-3 bg-white p-2 rounded-lg border shadow-sm" style={{ borderColor: 'var(--color-border)' }}>
            <DateFilter 
              value={dateFilter}
              onChange={setDateFilter}
            />
            {user?.role === 'Admin' && (
              <>
                <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--color-border)' }}></div>
                <Dropdown
                  icon={<Users size={16} className="text-muted" />}
                  value={userFilter}
                  onChange={setUserFilter}
                  options={uniqueUsers}
                />
              </>
            )}
          </div>
        </div>
      </div>



      {/* KPI Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
        <div className="card relative overflow-hidden group hover:shadow-md transition-shadow" style={{ padding: '1.5rem' }}>
          <div className="flex justify-between items-start mb-2">
            <div style={{ padding: '0.75rem', borderRadius: '12px', backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}>
              <Users size={24} />
            </div>
          </div>
          <h3 className="text-muted font-semibold text-sm uppercase tracking-wider mb-1">Total Leads</h3>
          <p className="text-3xl font-bold">{filteredLeads.length}</p>
        </div>
        
        <div className="card relative overflow-hidden group hover:shadow-md transition-shadow" style={{ padding: '1.5rem' }}>
          <div className="flex justify-between items-start mb-2">
            <div style={{ padding: '0.75rem', borderRadius: '12px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
              <CheckCircle size={24} />
            </div>
          </div>
          <h3 className="text-muted font-semibold text-sm uppercase tracking-wider mb-1">Converted</h3>
          <p className="text-3xl font-bold">{completedData.filter(l => l.status === 'Won' || (l.status === 'Completed' || l.status === 'Complete')).length}</p>
        </div>

        <div className="card relative overflow-hidden group hover:shadow-md transition-shadow" style={{ padding: '1.5rem' }}>
          <div className="flex justify-between items-start mb-2">
            <div style={{ padding: '0.75rem', borderRadius: '12px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
              <TrendingUp size={24} />
            </div>
          </div>
          <h3 className="text-muted font-semibold text-sm uppercase tracking-wider mb-1">Conversion Rate</h3>
          <p className="text-3xl font-bold">
            {filteredLeads.length > 0 ? ((completedData.filter(l => l.status === 'Won' || (l.status === 'Completed' || l.status === 'Complete')).length / filteredLeads.length) * 100).toFixed(1) : 0}%
          </p>
        </div>

        <div className="card relative overflow-hidden group hover:shadow-md transition-shadow" style={{ padding: '1.5rem' }}>
          <div className="flex justify-between items-start mb-2">
            <div style={{ padding: '0.75rem', borderRadius: '12px', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
              <PhoneCall size={24} />
            </div>
          </div>
          <h3 className="text-muted font-semibold text-sm uppercase tracking-wider mb-1">Active Follow-ups</h3>
          <p className="text-3xl font-bold">{allFollowUpCustomers.filter(f => (f.status !== 'Completed' && f.status !== 'Complete') && f.status !== 'Won').length}</p>
        </div>
      </div>

      {/* Visual Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
        
        {/* Leads By Source Bar Chart */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 className="text-muted font-semibold text-sm uppercase tracking-wider mb-4">Leads by Source</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: '1.5rem', columnGap: '1.5rem', marginTop: '1rem' }}>
            {topSources.map(([source, count], i) => {
              const colors = ['#3b82f6', '#6366f1', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e'];
              const color = colors[i % colors.length];
              const percentage = Math.round((count / (filteredLeads.length || 1)) * 100);
              return (
                <div key={source} className="flex flex-col gap-2">
                  <div className="flex justify-between items-end">
                    <span className="font-semibold text-sm">{source}</span>
                    <span className="font-bold" style={{ color: color }}>{count}</span>
                  </div>
                  <div style={{ height: '8px', width: '100%', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${percentage}%`, height: '100%', backgroundColor: color, borderRadius: '4px' }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Breakdown Bar Chart */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 className="text-muted font-semibold text-sm uppercase tracking-wider mb-4">Status Breakdown</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginTop: '1rem' }}>
            {topStatuses.map(([status, count], i) => {
              const stageColors = getStatusColors(status);
              const color = stageColors.dot;
              const percentage = Math.round((count / (filteredLeads.length || 1)) * 100);
              return (
                <div key={status}>
                  <div className="flex justify-between text-sm mb-2 font-semibold"><span>{status}</span><span style={{ color }}>{count}</span></div>
                  <div style={{ width: '100%', backgroundColor: '#f1f5f9', height: '0.5rem', borderRadius: '9999px', overflow: 'hidden' }}>
                    <div style={{ backgroundColor: color, height: '100%', borderRadius: '9999px', width: `${percentage}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Customer Lists Section */}
      <h3 className="font-semibold text-lg text-main mb-4 mt-8">Customer Lists</h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* New Leads List */}
        <div className="card" style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="flex justify-between items-center bg-blue-50" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)' }}>
            <h4 className="font-semibold text-blue-600 flex items-center gap-2"><Users size={16} /> New Leads</h4>
            <button onClick={() => handleDownloadList('new_leads', allNewLeads)} className="text-blue-600 flex items-center gap-1 text-xs font-semibold shadow-sm" style={{ padding: '0.35rem 0.75rem', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', backgroundColor: 'white' }}>
              <Download size={12} /> Download Excel
            </button>
          </div>
          <ul className="text-sm flex-1">
            {newLeads.length > 0 ? newLeads.map(c => (
              <li key={c.id} className="hover:bg-slate-50" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', transition: 'background-color 0.2s' }} onClick={() => handleView(c)}>
                <div className="font-bold text-main">{c.name}</div>
                <div className="flex justify-between text-xs text-muted mt-1">
                  <span>{c.contact} ({c.phone})</span>
                  <span className="bg-slate-100 px-1 rounded">{c.source}</span>
                </div>
              </li>
            )) : <li className="p-4 text-center text-muted italic">No records found.</li>}
          </ul>
          <div className="flex justify-between items-center bg-slate-50 p-3 border-t border-slate-100 mt-auto">
            <button 
              className="btn" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "white", border: "1px solid var(--color-border)" }}
              onClick={() => setNewLeadsPage(p => Math.max(1, p - 1))} disabled={newLeadsPage === 1}
            >Prev</button>
            <span className="text-xs text-muted">Page {newLeadsPage} of {Math.max(1, Math.ceil(allNewLeads.length / listItemsPerPage))}</span>
            <button 
              className="btn" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "white", border: "1px solid var(--color-border)" }}
              onClick={() => setNewLeadsPage(p => Math.min(Math.max(1, Math.ceil(allNewLeads.length / listItemsPerPage)), p + 1))} disabled={newLeadsPage >= Math.max(1, Math.ceil(allNewLeads.length / listItemsPerPage))}
            >Next</button>
          </div>
        </div>

        {/* Follow-up Customers List */}
        <div className="card" style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="flex justify-between items-center bg-blue-50" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)' }}>
            <h4 className="font-semibold text-blue-600 flex items-center gap-2"><PhoneCall size={16} /> Follow-ups</h4>
            <button onClick={() => handleDownloadList('followups', allFollowUpCustomers)} className="text-primary flex items-center gap-1 text-xs font-semibold shadow-sm" style={{ padding: '0.35rem 0.75rem', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: '6px', backgroundColor: 'white', color: 'var(--color-primary)' }}>
              <Download size={12} /> Download Excel
            </button>
          </div>
          <ul className="text-sm flex-1">
            {followUpCustomers.length > 0 ? followUpCustomers.map(c => (
              <li key={c.id} className="hover:bg-slate-50" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', transition: 'background-color 0.2s' }} onClick={() => handleView(c)}>
                <div className="font-bold text-main">{c.name}</div>
                <div className="text-xs text-muted mt-1">Next: {c.nextFollowUp}</div>
              </li>
            )) : <li className="p-4 text-center text-muted italic">No records found.</li>}
          </ul>
          <div className="flex justify-between items-center bg-slate-50 p-3 border-t border-slate-100 mt-auto">
            <button 
              className="btn" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "white", border: "1px solid var(--color-border)" }}
              onClick={() => setFollowupPage(p => Math.max(1, p - 1))} disabled={followupPage === 1}
            >Prev</button>
            <span className="text-xs text-muted">Page {followupPage} of {Math.max(1, Math.ceil(allFollowUpCustomers.length / listItemsPerPage))}</span>
            <button 
              className="btn" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "white", border: "1px solid var(--color-border)" }}
              onClick={() => setFollowupPage(p => Math.min(Math.max(1, Math.ceil(allFollowUpCustomers.length / listItemsPerPage)), p + 1))} disabled={followupPage >= Math.max(1, Math.ceil(allFollowUpCustomers.length / listItemsPerPage))}
            >Next</button>
          </div>
        </div>

        {/* Completed Customers List */}
        <div className="card" style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="flex justify-between items-center bg-success bg-opacity-5" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)' }}>
            <h4 className="font-semibold text-success flex items-center gap-2"><CheckCircle size={16} /> Completed</h4>
            <button onClick={() => handleDownloadList('completed', allCompletedCustomers)} className="text-success flex items-center gap-1 text-xs font-semibold shadow-sm" style={{ padding: '0.35rem 0.75rem', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '6px', backgroundColor: 'white' }}>
              <Download size={12} /> Download Excel
            </button>
          </div>
          <ul className="text-sm flex-1">
            {completedCustomers.length > 0 ? completedCustomers.map(c => (
              <li key={c.id} className="hover:bg-slate-50" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', transition: 'background-color 0.2s' }} onClick={() => handleView(c)}>
                <div className="font-bold text-main">{c.name}</div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-muted">{c.completionDate}</span>
                </div>
              </li>
            )) : <li className="p-4 text-center text-muted italic">No records found.</li>}
          </ul>
          <div className="flex justify-between items-center bg-slate-50 p-3 border-t border-slate-100 mt-auto">
            <button 
              className="btn" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "white", border: "1px solid var(--color-border)" }}
              onClick={() => setCompletedPage(p => Math.max(1, p - 1))} disabled={completedPage === 1}
            >Prev</button>
            <span className="text-xs text-muted">Page {completedPage} of {Math.max(1, Math.ceil(allCompletedCustomers.length / listItemsPerPage))}</span>
            <button 
              className="btn" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "white", border: "1px solid var(--color-border)" }}
              onClick={() => setCompletedPage(p => Math.min(Math.max(1, Math.ceil(allCompletedCustomers.length / listItemsPerPage)), p + 1))} disabled={completedPage >= Math.max(1, Math.ceil(allCompletedCustomers.length / listItemsPerPage))}
            >Next</button>
          </div>
        </div>

        {/* Deliveries List */}
        <div className="card" style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="flex justify-between items-center bg-purple-50" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)' }}>
            <h4 className="font-semibold text-purple-600 flex items-center gap-2"><Truck size={16} /> Deliveries</h4>
            <button onClick={() => handleDownloadList('deliveries', allDeliveries)} className="text-purple-600 flex items-center gap-1 text-xs font-semibold shadow-sm" style={{ padding: '0.35rem 0.75rem', border: '1px solid rgba(147, 51, 234, 0.3)', borderRadius: '6px', backgroundColor: 'white' }}>
              <Download size={12} /> Download Excel
            </button>
          </div>
          <ul className="text-sm flex-1">
            {deliveriesList.length > 0 ? deliveriesList.map(c => (
              <li key={c.id} className="hover:bg-slate-50" style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', transition: 'background-color 0.2s' }} onClick={() => { setViewingDelivery(c.original); setShowDeliveryModal(true); }}>
                <div className="font-bold text-main">{c.name}</div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-muted">{c.contact} {c.phone && c.phone !== 'N/A' ? `(${c.phone})` : ''}</span>
                  <span className="bg-purple-100 text-purple-700 px-1 rounded font-medium">{c.source}</span>
                </div>
              </li>
            )) : <li className="p-4 text-center text-muted italic">No records found.</li>}
          </ul>
          <div className="flex justify-between items-center bg-slate-50 p-3 border-t border-slate-100 mt-auto">
            <button 
              className="btn" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "white", border: "1px solid var(--color-border)" }}
              onClick={() => setDeliveryPage(p => Math.max(1, p - 1))} disabled={deliveryPage === 1}
            >Prev</button>
            <span className="text-xs text-muted">Page {deliveryPage} of {Math.max(1, Math.ceil(allDeliveries.length / listItemsPerPage))}</span>
            <button 
              className="btn" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "white", border: "1px solid var(--color-border)" }}
              onClick={() => setDeliveryPage(p => Math.min(Math.max(1, Math.ceil(allDeliveries.length / listItemsPerPage)), p + 1))} disabled={deliveryPage >= Math.max(1, Math.ceil(allDeliveries.length / listItemsPerPage))}
            >Next</button>
          </div>
        </div>

      </div>

      
      {showDeliveryModal && viewingDelivery && (
        <ViewDeliveryModal 
          delivery={viewingDelivery}
          onClose={() => setShowDeliveryModal(false)}
          onTakeAction={() => {}} 
          canTakeAction={false}
        />
      )}

      {showModal && viewingLead && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 50,
          padding: '2rem',
          animation: 'fadeIn 0.2s'
        }}>
          <div className="hide-scrollbar" style={{
            backgroundColor: 'var(--color-bg-card)',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh', overflow: 'hidden',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <AddLead 
              onClose={handleCloseModal} 
              onSave={() => { handleCloseModal(); fetchLeadsAndFollowups(); }}
              initialData={viewingLead}
              isViewMode={isViewMode}
              onEdit={(lead) => { setShowModal(false); setTimeout(() => { setViewingLead(lead); setIsViewMode(false); setShowModal(true); }, 0); }}
              onDelete={(id) => handleDelete(id)}
              onAction={(lead) => {
                setActionLead(lead);
                setShowActionModal(true);
              }}
            />
          </div>
        </div>
      )}

      {showActionModal && actionLead && (
        <TakeActionModal 
          lead={actionLead} 
          onClose={() => setShowActionModal(false)}
          onSuccess={() => {
            setShowActionModal(false);
            fetchLeadsAndFollowups();
            if (viewingLead) {
              handleView({ id: viewingLead.id });
            }
          }} 
        />
      )}

      {deleteLeadId && (
        <div className="modal-backdrop">
          <div className="modal-content fade-in" style={{ maxWidth: '400px', padding: '2rem', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} className="text-danger" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete Lead</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this lead? This action cannot be undone and all associated follow-ups will be permanently removed.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn btn-secondary flex-1" onClick={() => setDeleteLeadId(null)}>
                Cancel
              </button>
              <button className="btn btn-danger flex-1" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
